import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Money } from '../components/ui/Money';
import { CircleDollarSign, Search, ArrowUpDown, TrendingUp, TrendingDown, Clock, ChevronDown, ChevronUp, BarChart3, FileSpreadsheet, FileText, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { format } from 'date-fns';
import { PromotionStatusActions, STATUS_META } from '../components/employee-form/PromotionStatusActions';
import { BulkSalaryRevision } from '../components/salary/BulkSalaryRevision';

const API = process.env.REACT_APP_BACKEND_URL;

const SalaryRevisions = () => {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('effective_date');
  const [sortDir, setSortDir] = useState('desc');
  const [deptFilter, setDeptFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/employees/salary-revisions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRevisions(res.data || []);
    } catch (err) {
      console.error('Salary revisions load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const isViewer = (JSON.parse(localStorage.getItem('user') || '{}').role) === 'viewer';

  const downloadRevert = async (r, fmt) => {
    try {
      const res = await axios.get(`${API}/api/employees/${r.employee_id}/promotion/${r.id}/letter?format=${fmt}&kind=revert`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Salary_Revision_Reversal_${(r.employee_name || '').replace(/\s+/g, '_')}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { /* toast handled elsewhere */ }
  };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'employee_name' || key === 'department' ? 'asc' : 'desc');
    }
  };

  const departments = useMemo(() => {
    const set = new Set(revisions.map(r => r.department).filter(Boolean));
    return Array.from(set).sort();
  }, [revisions]);

  const hasFilters = deptFilter !== 'all' || dateFrom || dateTo;

  // Scoped = department + date-range filters (drives stats, trend chart & export)
  const scoped = useMemo(() => {
    return revisions.filter(r => {
      if (deptFilter !== 'all' && r.department !== deptFilter) return false;
      const eff = (r.effective_date || '').slice(0, 10);
      if (dateFrom && (!eff || eff < dateFrom)) return false;
      if (dateTo && (!eff || eff > dateTo)) return false;
      return true;
    });
  }, [revisions, deptFilter, dateFrom, dateTo]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = scoped;
    if (q) {
      rows = rows.filter(r =>
        (r.employee_name || '').toLowerCase().includes(q) ||
        (r.employee_code || '').toLowerCase().includes(q) ||
        (r.department || '').toLowerCase().includes(q) ||
        (r.new_position || '').toLowerCase().includes(q) ||
        (r.previous_position || '').toLowerCase().includes(q) ||
        (r.approved_by || '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let av = a[sortBy];
      let bv = b[sortBy];
      if (typeof av === 'string') av = (av || '').toLowerCase();
      if (typeof bv === 'string') bv = (bv || '').toLowerCase();
      if (av == null) av = -Infinity;
      if (bv == null) bv = -Infinity;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [scoped, search, sortBy, sortDir]);

  const stats = useMemo(() => {
    const live = scoped.filter(r => r.status !== 'reverted');
    const total = live.length;
    const increases = live.filter(r => r.change > 0).length;
    const avgPct = (() => {
      const withPct = live.filter(r => r.pct_increase != null);
      if (!withPct.length) return null;
      return Math.round((withPct.reduce((s, r) => s + r.pct_increase, 0) / withPct.length) * 10) / 10;
    })();
    return { total, increases, avgPct };
  }, [scoped]);

  // Monthly total salary uplift (sum of change), oldest → newest, for the trend chart
  const trend = useMemo(() => {
    const byMonth = {};
    for (const r of scoped) {
      if (r.status === 'reverted') continue;
      const ym = (r.effective_date || '').slice(0, 7); // YYYY-MM
      if (!ym) continue;
      if (!byMonth[ym]) byMonth[ym] = { uplift: 0, count: 0 };
      byMonth[ym].uplift += (r.change || 0);
      byMonth[ym].count += 1;
    }
    return Object.keys(byMonth)
      .sort()
      .map(ym => {
        const [y, m] = ym.split('-');
        const d = new Date(Number(y), Number(m) - 1, 1);
        return {
          ym,
          label: format(d, 'MMM yy'),
          uplift: Math.round(byMonth[ym].uplift),
          count: byMonth[ym].count,
        };
      });
  }, [scoped]);

  const clearFilters = () => {
    setDeptFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const handleExport = async (fmt) => {
    setExporting(fmt);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ format: fmt });
      if (deptFilter !== 'all') params.append('department', deptFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await axios.get(`${API}/api/employees/salary-revisions/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename=([^;]+)/);
      const ext = fmt === 'pdf' ? 'pdf' : 'xlsx';
      const filename = match ? match[1].replace(/"/g, '').trim() : `Salary_Revisions.${ext}`;
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting('');
    }
  };

  const SortHead = ({ label, k, align = 'left' }) => (
    <th
      className={`py-2.5 px-3 font-semibold text-gray-500 cursor-pointer select-none hover:text-gray-800 whitespace-nowrap text-${align}`}
      onClick={() => toggleSort(k)}
      data-testid={`salrev-sort-${k}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {sortBy === k
          ? (sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)
          : <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </span>
    </th>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="salary-revisions-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-emerald-100 flex items-center justify-center">
          <CircleDollarSign className="h-6 w-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Salary Revisions</h1>
          <p className="text-sm text-gray-500">Quick view of every salary revision & promotion across all staff</p>
        </div>
        <div className="ml-auto flex gap-2">
          {!isViewer && <BulkSalaryRevision onDone={load} />}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={() => handleExport('excel')}
            disabled={exporting !== '' || filtered.length === 0}
            data-testid="salrev-export-excel"
          >
            <FileSpreadsheet className="h-4 w-4" /> {exporting === 'excel' ? 'Exporting…' : 'Excel'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"
            onClick={() => handleExport('pdf')}
            disabled={exporting !== '' || filtered.length === 0}
            data-testid="salrev-export-pdf"
          >
            <FileText className="h-4 w-4" /> {exporting === 'pdf' ? 'Exporting…' : 'PDF'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm" data-testid="salrev-filters">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="min-w-[180px]">
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Department</label>
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="h-9" data-testid="salrev-dept-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Effective From</label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-[160px]" data-testid="salrev-date-from" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Effective To</label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-[160px]" data-testid="salrev-date-to" />
          </div>
          {hasFilters && (
            <Button variant="ghost" size="sm" className="gap-1 text-gray-500 h-9" onClick={clearFilters} data-testid="salrev-clear-filters">
              <X className="h-3.5 w-3.5" /> Clear
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-sm" data-testid="salrev-stat-total">
          <CardContent className="p-4">
            <p className="text-xs text-gray-400">Total Revisions</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="salrev-stat-increases">
          <CardContent className="p-4">
            <p className="text-xs text-gray-400">Salary Increases</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.increases}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="salrev-stat-avg">
          <CardContent className="p-4">
            <p className="text-xs text-gray-400">Avg. Increase</p>
            <p className="text-2xl font-bold text-sky-600">{stats.avgPct != null ? `${stats.avgPct > 0 ? '+' : ''}${stats.avgPct}%` : '—'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Revision Trends */}
      {!loading && trend.length > 0 && (
        <Card className="border-0 shadow-sm" data-testid="salrev-trend-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="h-4 w-4 text-emerald-600" />
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Revision Trends — Total Salary Uplift by Month</h3>
            </div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend} margin={{ top: 8, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    cursor={{ fill: 'rgba(16,185,129,0.06)' }}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '11px' }}
                    formatter={(value, name, props) => [`AED ${Number(value).toLocaleString()} (${props.payload.count} revision${props.payload.count > 1 ? 's' : ''})`, 'Uplift']}
                  />
                  <Bar dataKey="uplift" radius={[4, 4, 0, 0]} maxBarSize={48}>
                    {trend.map((entry, i) => (
                      <Cell key={i} fill={entry.uplift >= 0 ? '#10b981' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, code, department or position..."
          className="pl-9"
          data-testid="salrev-search-input"
        />
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-emerald-600" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              {revisions.length === 0 ? 'No salary revisions recorded yet' : 'No revisions match your search'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/60">
                    <SortHead label="Employee" k="employee_name" />
                    <SortHead label="Department" k="department" />
                    <th className="py-2.5 px-3 font-semibold text-gray-500 text-left whitespace-nowrap">Position (Old → New)</th>
                    <SortHead label="Old Salary" k="previous_salary" align="right" />
                    <SortHead label="New Salary" k="new_salary" align="right" />
                    <SortHead label="Change" k="change" align="right" />
                    <SortHead label="Effective" k="effective_date" align="right" />
                    <SortHead label="Approved By" k="approved_by" />
                    <th className="py-2.5 px-3 font-semibold text-gray-500 text-left whitespace-nowrap">Reason</th>
                    <th className="py-2.5 px-3 font-semibold text-gray-500 text-left whitespace-nowrap">Status / Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    const up = r.change >= 0;
                    return (
                      <tr key={r.id || i} className="border-b border-gray-50 hover:bg-emerald-50/40" data-testid={`salrev-row-${i}`}>
                        <td className="py-2.5 px-3">
                          <div className="font-medium text-gray-900 flex items-center gap-1.5">
                            {r.employee_name}
                            {r.pending_apply && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded uppercase font-semibold">
                                <Clock className="h-2.5 w-2.5" /> Scheduled
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-gray-400">{r.employee_code}</div>
                        </td>
                        <td className="py-2.5 px-3 text-gray-600">{r.department}</td>
                        <td className="py-2.5 px-3 text-gray-700">
                          <span className="text-gray-400">{r.previous_position || '—'}</span>
                          <span className="mx-1 text-gray-300">→</span>
                          <span className="font-medium text-emerald-700">{r.new_position || '—'}</span>
                        </td>
                        <td className="py-2.5 px-3 text-right text-gray-500"><Money value={r.previous_salary} /></td>
                        <td className="py-2.5 px-3 text-right font-medium text-gray-900"><Money value={r.new_salary} /></td>
                        <td className="py-2.5 px-3 text-right whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 font-semibold ${up ? 'text-emerald-600' : 'text-red-600'}`}>
                            {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                            {up ? '+' : ''}<Money value={r.change} />
                          </span>
                          {r.pct_increase != null && (
                            <div className="text-[11px] text-gray-400">{r.pct_increase > 0 ? '+' : ''}{r.pct_increase}%</div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right text-gray-600 whitespace-nowrap">
                          {r.effective_date ? format(new Date(r.effective_date), 'dd MMM yyyy') : '—'}
                        </td>
                        <td className="py-2.5 px-3 text-gray-600 whitespace-nowrap">{r.approved_by || '—'}</td>
                        <td className="py-2.5 px-3 text-gray-500 max-w-[220px] truncate" title={r.reason}>{r.reason || '—'}</td>
                        <td className="py-2.5 px-3 min-w-[230px]">
                          {STATUS_META[r.status] ? (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${STATUS_META[r.status].cls}`} data-testid={`salrev-status-${i}`}>{STATUS_META[r.status].label}</span>
                          ) : <span className="text-[11px] text-gray-400" data-testid={`salrev-status-${i}`}>Active</span>}
                          <PromotionStatusActions employeeId={r.employee_id} promo={r} index={`rev-${i}`} isViewer={isViewer}
                            onChanged={load} onDownload={(p, fmt) => downloadRevert(r, fmt)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {!loading && filtered.length > 0 && (
        <p className="text-xs text-gray-400 text-right">Showing {filtered.length} of {revisions.length} revisions</p>
      )}
    </div>
  );
};

export default SalaryRevisions;
